// 11 §7 — ACTION TILES (mockup bottom bar, UI specifics 02): number key, Oryx icon, name and a
// sub-line in the hero's own numbers ("up to 4", "4 dmg · reach 1"): a weapon attack wears the
// sprite of the weapon in hand and its tooltip names it (`core/sim/weapon.ts`). States: default, hover, selected
// (gold), queued in slot N (blue with the slot badge), not for this slot (dimmed), target out of
// range (red, from the legality probe). The tiles show even with no slot open; clicking one then
// opens the first empty slot that may hold it.
import { useEffect, useState } from 'react';
import { HotkeyKbd, OryxIcon, Tooltip } from '../components';
import { ariaKeys, hotbarId, keyLabel, useHotkeyOverrides } from '../../core/input/hotkeys';
import { useAuthoring } from '../../core/stores/authoring';
import { usePlayback } from '../../core/stores/playback';
import { useEncounter } from '../../core/encounter/session';
import { useView } from '../../core/stores/view';
import { useWorldItems } from '../../core/subscriptions/hooks';
import { MOVE, type ActionDef, type Glyph } from '../../core/sim/actions';
import { abilityTerms } from '../../core/sim/describe';
import { SLOTS, type Slot, type UnitState } from '../../core/sim/types';
import { BASIC_ATTACK, heldWeapon, strengthOf } from '../../core/sim/weapon';
import { ItemArtIcon } from '../inventory/ItemCard';

/** Glyph → the board's intent colour family (game/intent/palette) and an Oryx pick (U8). */
const TILE: Record<Glyph, { tone: 'move' | 'attack' | 'area' | 'support' | 'neutral'; icon?: string; mark: string }> = {
  move: { tone: 'move', icon: 'item_287', mark: '➜' }, attack: { tone: 'attack', icon: 'item_sword', mark: '⚔' }, beam: { tone: 'attack', icon: 'item_bow', mark: '➶' },
  area: { tone: 'area', icon: 'item_154', mark: '✺' }, shield: { tone: 'support', icon: 'item_shield_round', mark: '⛨' }, guard: { tone: 'support', icon: 'item_shield_round', mark: '⛨' },
  heal: { tone: 'support', icon: 'item_heart', mark: '♥' }, ward: { tone: 'support', icon: 'item_potion_blue', mark: '✧' },
  interact: { tone: 'neutral', icon: 'item_key_gold', mark: '⚷' }, neutral: { tone: 'neutral', mark: '◦' },
};

/** docs/40: Use Item's tile — the red flask. */
const ITEM_TILE = { tone: 'support' as const, icon: 'item_potion_red', mark: '⚗' };

function subLine(a: ActionDef, unit: UnitState, cooling?: number, count?: number): string {
  if (cooling) return `recovering · ${cooling} turn${cooling === 1 ? '' : 's'}`;
  // docs/72 IT18: a piece in the bag says how many are left; a worn piece's active says it is worn
  if (a.item?.used) return `×${count ?? 0} · bonus · bag`;
  // the hero's own pace (a fighter in mail walks 3, a Dash adds to it), not the catalogue's 4
  if (a.id === MOVE.id) return `up to ${unit.move ?? a.range}`;
  // a Dash moves no one by itself: it adds squares to this turn's walks
  if (a.glyph === 'move' && a.args === 'none') return [a.amount ? `+${a.amount} move` : '', a.slots.length === 1 && a.slots[0] === 'B' ? 'bonus' : ''].filter(Boolean).join(' · ') || 'self';
  if (a.glyph === 'move') return `up to ${a.range}`;
  if (a.args === 'item') return 'bonus · bag (I)';
  if (a.args === 'none' && a.dmg === undefined) return a.id === 2 ? 'skip this slot' : a.slots.length === 1 && a.slots[0] === 'B' ? 'bonus' : a.aura !== undefined ? (a.aura <= 1 ? 'around you' : `within ${a.aura}`) : 'self';
  // the hero's own numbers: what it hits for before guard, and how far
  const st = strengthOf(a, unit.arms, heldWeapon(unit));
  const support = a.glyph === 'heal' || a.glyph === 'ward' || a.glyph === 'shield' || a.glyph === 'guard';
  const amt = st.damage !== undefined ? `${st.damage} dmg` : a.amount !== undefined ? (support ? `+${a.amount}` : `${a.amount}`) : undefined;
  const round = a.aura === undefined ? undefined : a.aura <= 1 ? 'around you' : `within ${a.aura}`;
  const far = st.range ? (st.range.melee ? `reach ${st.range.n}` : `range ${st.range.n}`) : a.range > 1 ? `range ${a.range}` : a.args === 'none' ? round ?? 'self' : 'reach 1';
  return [amt, far].filter(Boolean).join(' · ');
}

/** The tooltip's weapon line: "With your Iron Hand Axe: 4 damage (axe 3 + power 1) before guard, reach 1." */
function weaponLine(a: ActionDef, unit: UnitState): string | undefined {
  const held = heldWeapon(unit);
  const st = strengthOf(a, unit.arms, held);
  if (st.damage === undefined) return undefined;
  const far = st.range ? (st.range.melee ? `reach ${st.range.n}` : `range ${st.range.n}`) : undefined;
  const parts = st.parts && st.parts !== String(st.damage) ? ` (${st.parts})` : '';
  const head = st.weapon && held ? (held.bare ? `With ${held.name}` : `With your ${held.name}`) : a.dmg?.kind === 'Spell' ? 'Spell' : 'Hits';
  return `${head}: ${st.damage} damage${parts} before the target's guard${far ? `, ${far}` : ''}.`;
}

export function Hotbar() {
  const keys = useHotkeyOverrides();
  const hotbar = useAuthoring((s) => s.hotbar);
  const action = useAuthoring((s) => s.action);
  const slot = useAuthoring((s) => s.slot);
  const heroId = useAuthoring((s) => s.selectedHero);
  const state = useAuthoring((s) => s.machineState);
  const playing = usePlayback((s) => s.playing);
  const probe = useAuthoring((s) => s.probe);
  const local = useAuthoring((s) => s.local);
  const rows = useAuthoring((s) => s.rows);
  const pickAction = useAuthoring((s) => s.pickAction);
  const barActions = useAuthoring((s) => s.barActions);
  const hero = useEncounter((s) => (heroId === undefined ? undefined : s.snapshot?.units.find((u) => u.heroId === heroId)));
  const bag = useEncounter((s) => (hero ? s.snapshot?.bags?.find((b) => b.unit === hero.unitId) : undefined));
  // the World's item rows, so the hand each hero holds is in the cache (`heldWeapon`) and the bar redraws when it changes
  useWorldItems(useEncounter((s) => s.worldId));
  const bagLeft = (def: number) => bag?.items.filter((i) => i.def === def && !i.used).length ?? 0;
  // the tile the pointer (or focus) is on, for its tooltip
  const [tip, setTip] = useState<{ el: HTMLElement; id: number }>();
  // docs/48 AR4: a range preview never outlives the bar (or the hero) it came from
  useEffect(() => () => { useView.getState().setHoverAction(undefined); setTip(undefined); }, [heroId]);
  if (!hero) return <div className="fk-hud__tiles fk-hud__tiles--empty" role="toolbar" aria-label="Actions"><span className="fk-hud__hint">Pick one of your heroes (Tab or click) to plan.</span></div>;

  // Everything the hero can do this pulse: the open slot's list, else the whole bar (docs/33 E3: the number keys match).
  const all: ActionDef[] = slot !== undefined ? hotbar : barActions();
  const queued = (id: number): Slot | undefined => SLOTS.find((s) => (local.get(`${heroId}:${s}`) ?? rows.get(`${heroId}:${s}`))?.action === id && id !== 2);
  const committed = [...rows.values()].some((i) => i.heroId === heroId && i.state === 'committed');
  // while the last turn plays out nothing is planned (the next turn's plan is not shown yet)
  const locked = committed || playing;
  const outOfRange = state.kind === 'ActionChosen' && probe && !probe.verdict.legal;
  const open = (a: ActionDef) => {
    // a second click on the chosen action puts it down again
    if (action === a.id && state.kind === 'ActionChosen') { useAuthoring.getState().send({ type: 'escape' }); useAuthoring.getState().send({ type: 'escape' }); return; }
    if (a.id === 2 && slot !== undefined) { useAuthoring.getState().send({ type: 'hold' }); return; }
    // docs/40: Use Item opens the bag; the item is chosen there
    if (a.args === 'item') { useView.getState().toggleInventory(); return; }
    pickAction(a.id);
  };

  const tipDef = tip ? all.find((a) => a.id === tip.id) : undefined;
  const tipCooling = tipDef ? hero.cooldowns?.[tipDef.id] : undefined;
  const tipWeapon = tipDef ? weaponLine(tipDef, hero) : undefined;
  const tipHeld = tipDef?.dmg ? heldWeapon(hero) : undefined;
  const tipTerms = tipDef ? [...abilityTerms(tipDef, hero.resource?.kind), ...(tipWeapon || tipDef.args === 'none' || tipDef.args === 'item' || !tipDef.range ? [] : [tipDef.glyph === 'move' ? `Up to ${tipDef.id === MOVE.id ? hero.move ?? tipDef.range : tipDef.range} squares` : tipDef.range <= 1 ? 'Reach: next to you' : `Range ${tipDef.range}`])].join(' · ') : '';
  const hover = (a: ActionDef, el: HTMLElement | undefined) => {
    useView.getState().setHoverAction(el ? a.id : undefined);
    setTip((t) => (el ? { el, id: a.id } : t?.id === a.id ? undefined : t));
  };

  return (
    <>
    {/* docs/83: always one row, from the left; a long list packs its tiles tighter, then scrolls */}
    <div className={`fk-hud__tiles${all.length > 8 ? ' fk-hud__tiles--dense' : ''}`} role="toolbar" aria-label="Actions" data-slot={slot === undefined ? '' : String(slot)}>
      {all.map((a, i) => {
        const t = a.args === 'item' ? ITEM_TILE : TILE[a.glyph] ?? TILE.neutral;
        const q = queued(a.id);
        const unfit = slot !== undefined ? false : !a.slots.some((s) => s === 1 || s === 2 || s === 'B');
        const selected = action === a.id;
        // the sim's cooldown left on this ability: the tile darkens by what is left and counts the turns down
        const cooling = hero.cooldowns?.[a.id];
        const cls = ['fk-hud__tile', `fk-hud__tile--${t.tone}`, selected ? (outOfRange ? 'fk-hud__tile--bad' : 'fk-hud__tile--on') : '', q !== undefined && !selected ? 'fk-hud__tile--queued' : '', unfit || locked ? 'fk-hud__tile--dim' : '', cooling ? 'fk-hud__tile--cooling' : '', a.id === 2 ? 'fk-hud__tile--hold' : ''].filter(Boolean).join(' ');
        const left = cooling ? Math.min(1, cooling / Math.max(1, a.cooldown ?? cooling)) : 0;
        return (
          <button key={a.id} type="button" className={`${cls} fk-hit`} style={{ ['--fk-i' as string]: i, ...(cooling ? { ['--fk-cd' as string]: left } : {}) }} aria-pressed={selected} onClick={() => open(a)}
            onPointerEnter={(e) => hover(a, e.currentTarget)} onPointerLeave={() => hover(a, undefined)}
            onFocus={(e) => hover(a, e.currentTarget)} onBlur={() => hover(a, undefined)} aria-keyshortcuts={a.hotkey ? ariaKeys(hotbarId(a.hotkey), keys) : undefined} disabled={locked}
            aria-label={`${a.name}${cooling ? `, recovering for ${cooling} more turn${cooling === 1 ? '' : 's'}` : ''}${q !== undefined ? `, queued in slot ${q}` : ''}`} aria-description={a.text}
            data-cooldown={cooling || undefined} data-item-def={a.item?.def} data-testid={a.item ? 'hotbar-item' : undefined}>
            {a.hotkey && keyLabel(hotbarId(a.hotkey), keys) && <span className="fk-hud__tile-key">{keyLabel(hotbarId(a.hotkey), keys)}</span>}
            {q !== undefined && <span className="fk-hud__tile-slot" aria-label={`in slot ${q}`}>{String(q)}</span>}
            <span className="fk-hud__tile-icon" aria-hidden="true">{a.item ? (a.item.glyph ? <OryxIcon name={a.item.glyph} scale={2} glyph={t.mark} /> : <ItemArtIcon def={a.item.def} scale={2} glyph={t.mark} />) : a.weaponIcon ? <ItemArtIcon def={a.weaponIcon.def} name={a.weaponIcon.name} scale={2} glyph={t.mark} /> : t.icon ? <OryxIcon name={t.icon} scale={2} glyph={t.mark} /> : <span className="fk-hud__tile-mark">{t.mark}</span>}</span>
            {a.item?.used && <span className="fk-hud__tile-count" aria-label={`${bagLeft(a.item.def)} left`}>{bagLeft(a.item.def)}</span>}
            {cooling ? <span className="fk-hud__tile-cd" aria-hidden="true">{cooling}</span> : null}
            <span className="fk-hud__tile-name">{a.name}</span>
            <span className="fk-hud__tile-sub">{subLine(a, hero, cooling, a.item ? bagLeft(a.item.def) : undefined)}</span>
          </button>
        );
      })}
    </div>
    {tipDef && (
      <Tooltip anchor={tip?.el} className="fk-hud__tip" title={<>{tipDef.name}{tipDef.hotkey && <HotkeyKbd id={hotbarId(tipDef.hotkey)} />}</>}
        terms={tipTerms || tipCooling || tipWeapon ? <>
          {tipCooling ? <div className="fk-hud__tip-cd">Recovering: ready again in {tipCooling} turn{tipCooling === 1 ? '' : 's'}.</div> : null}
          {tipWeapon ? <div className="fk-hud__tip-weapon" data-testid="tip-weapon">{tipWeapon}</div> : null}
          {tipTerms}
        </> : undefined}>
        {tipDef.id === BASIC_ATTACK && tipHeld ? `A plain blow with ${tipHeld.bare ? tipHeld.name : `your ${tipHeld.name}`}: it costs nothing and never needs to recover.` : tipDef.text ?? tipDef.name}
      </Tooltip>
    )}
    </>
  );
}
